import { useEffect, useContext, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

//context
import { AssessmentContext } from '../../context/AssessmentContext.js';

//helpers & scripts
import {
  showCustomDialog,
  getCustomDialogButtons,
  LEARNOSITY_EVENTS,
} from '../../scripts/learnosityScripts.js';
import * as Storage from '../common/Storage.js';
import useSessionStorage from '../../hooks/useSessionStorage.js';

import { IDLE_DIALOG } from '../../components/assessment/LearnosityDialogs.js';
import { LearnosityContainerObservable } from '../assessment/LearnosityContainer.js';
import CustomDialog from '../ui/feedback/CustomDialog.js';

const WakeDetector = () => {
  const { removeStorageItem, getStorageItem, setStorageItem } =
    useSessionStorage();

  const [wakeDetectorWorker, setWakeDetectorWorker] = useState();
  const { setExit } = useContext(AssessmentContext);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const IDLE_MODAL_ACTIONS = useCallback(() => {
    return [
      {
        id: 'idle_exitButton',
        label: t('common.exit'),
        handle: () => {
          setExit(true);
        },
        primary: true,
      },
    ];
  }, [setExit, t]);

  const processWakeupDetected = useCallback(
    (learnosityItemApp) => {
      //remove auth token, so tester can no longer continue
      removeStorageItem(Storage.STORAGE_AUTH_TOKEN);

      learnosityItemApp?.save({
        success: () => {
          console.log('Save after wakeup is successful.');
        },
        error: (event) => {
          console.log('Save after wakeup failed.');
        },
      });

      showCustomDialog(
        learnosityItemApp,
        IDLE_DIALOG(
          getCustomDialogButtons(IDLE_MODAL_ACTIONS(), learnosityItemApp)
        )
      );
      setOpen(true);
    },
    [IDLE_MODAL_ACTIONS, removeStorageItem]
  );

  const eventHandler = useCallback(
    async ({ appInstance, eventName, error, ready }) => {
      if (eventName === LEARNOSITY_EVENTS.TEST_READY) {
        if (wakeDetectorWorker) {
          wakeDetectorWorker.terminate();
        }
        // Create a new worker
        console.log('creating new wakeDetectWorker');
        removeStorageItem(Storage.STORAGE_LAST_RECORDED_WAKE_TIME);
        let newWakeDetectorWorker = new Worker('wakeDetectWorker.js');
        //This is the only way we can pass env variable to a webworker. note web worker has different scope and can't access global/window objects
        newWakeDetectorWorker.postMessage({
          event: 'start',
          checkInterval: parseInt(window.env.REACT_APP_SLEEP_CHECK_INTERVAL_MS),
          sleepDuration: parseInt(window.env.REACT_APP_SLEEP_DURATION_MS),
        });

        newWakeDetectorWorker.onmessage = (event) => {
          if (event?.data?.msg === 'wakeup') {
            let lastRecordedWakeTime = getStorageItem(
              Storage.STORAGE_LAST_RECORDED_WAKE_TIME
            );

            //only process the first sleep/wake event, subsequent events don't matter anymore
            if (!lastRecordedWakeTime) {
              processWakeupDetected(appInstance);
              setStorageItem(
                Storage.STORAGE_LAST_RECORDED_WAKE_TIME,
                event.data.lastRecordedWakeTime
              );
            }
          }
        };
        setWakeDetectorWorker(newWakeDetectorWorker);
      }
    },
    [
      wakeDetectorWorker,
      setWakeDetectorWorker,
      processWakeupDetected,
      getStorageItem,
      setStorageItem,
      removeStorageItem,
    ]
  );

  useEffect(() => {
    LearnosityContainerObservable.subscribe(
      eventHandler,
      'wakeDetectorHandler'
    );
  }, [eventHandler]);

  useEffect(() => {
    //handles unmounting only
    return () => {
      setWakeDetectorWorker((prevWorker) => {
        if (prevWorker) {
          prevWorker.terminate();
        }
        return null;
      });
    };
  }, []);

  return (
    <CustomDialog
      open={open}
      dialogProps={{
        title: IDLE_DIALOG().header,
        message: IDLE_DIALOG().body,
        actions: IDLE_MODAL_ACTIONS(),
      }}
    />
  );
};

export default WakeDetector;
